import { useCallback, useEffect, useState } from 'react'
import { Row, Col, Select, Space, Spin, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { operatorsApi, statsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { seesAllLeads } from '../constants/roles'
import {
    StatAccountsIcon,
    StatOperatorsIcon,
    StatMessagesIcon,
    StatContactsIcon
} from '../components/Icons'

interface DashboardStats {
    total_accounts: number
    active_accounts: number
    banned_accounts: number
    total_operators: number
    online_operators: number
    total_messages: number
    messages_today: number
    total_contacts: number
}

export default function Dashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [operators, setOperators] = useState<Array<{ id: number; username: string; full_name?: string | null; department?: string | null }>>([])
    const [selectedOperatorId, setSelectedOperatorId] = useState<number | undefined>()
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const isSuperAdmin = user?.role === 'superadmin'
    const isAdmin = seesAllLeads(user?.role)

    const fetchStats = useCallback(async () => {
        setLoading(true)
        try {
            const response = await statsApi.getDashboard(selectedOperatorId)
            setStats(response.data)
        } catch (error) {
            message.error('Ошибка загрузки статистики')
        } finally {
            setLoading(false)
        }
    }, [selectedOperatorId])

    useEffect(() => {
        fetchStats()
    }, [fetchStats])

    useEffect(() => {
        if (!isAdmin) return
        operatorsApi.getAll(0, 500)
            .then((response) => setOperators(response.data))
            .catch(() => setOperators([]))
    }, [isAdmin])

    const StatCard = ({
        title,
        value,
        subtitle,
        icon,
        color,
        onClick,
        delay = 0
    }: {
        title: string
        value: number | string
        subtitle?: string
        icon: React.ReactNode
        color: string
        onClick?: () => void
        delay?: number
    }) => (
        <div
            className="stat-card-animated"
            onClick={onClick}
            style={{
                background: 'var(--bg-surface)',
                borderRadius: 20,
                padding: 28,
                cursor: onClick ? 'pointer' : 'default',
                border: '1px solid var(--border)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: `fadeInUp 0.5s ease-out ${delay}s both`,
                minHeight: 160,
            }}
        >

            <div style={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                background: `linear-gradient(135deg, ${color}20 0%, transparent 70%)`,
                borderRadius: '50%',
                transition: 'all 0.4s ease',
            }} />


            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${color}, ${color}80)`,
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.3s ease',
            }} className="hover-line" />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
                <div>
                    <div style={{
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: 12,
                        whiteSpace: 'nowrap',
                    }}>
                        {title}
                    </div>
                    <div style={{
                        fontSize: 42,
                        fontWeight: 800,
                        color: 'var(--text-primary)',
                        lineHeight: 1,
                        letterSpacing: '-0.03em',
                        marginBottom: 8,
                    }}>
                        {value}
                    </div>
                    {subtitle && (
                        <div style={{
                            fontSize: 13,
                            color: color,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}>
                            <span style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: color,
                                animation: 'pulse 2s ease-in-out infinite',
                            }} />
                            {subtitle}
                        </div>
                    )}
                </div>
                <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                    border: `1px solid ${color}20`,
                }}>
                    {icon}
                </div>
            </div>


            {onClick && (
                <div style={{
                    position: 'absolute',
                    right: 16,
                    bottom: 16,
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    opacity: 0.5,
                    transition: 'opacity 0.3s ease',
                }} className="click-hint">
                    Нажмите для перехода →
                </div>
            )}
        </div>
    )

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 'calc(100vh - 68px)'
            }}>
                <Spin size="large" />
            </div>
        )
    }

    return (
        <div style={{ padding: 32 }}>

            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 36, animation: 'fadeIn 0.5s ease-out' }} align="start" wrap>
                <div>
                    <h1 style={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: 'var(--text-primary)',
                        marginBottom: 8,
                        letterSpacing: '-0.02em',
                    }}>
                        Добро пожаловать! 👋
                    </h1>
                    <p style={{
                        fontSize: 15,
                        color: 'var(--text-muted)',
                        margin: 0,
                    }}>
                        Статистика по лидам, сообщениям и рабочим аккаунтам
                    </p>
                </div>
                {isAdmin && (
                    <Select
                        allowClear
                        value={selectedOperatorId}
                        placeholder="Все сотрудники"
                        style={{ width: 280 }}
                        optionFilterProp="label"
                        showSearch
                        options={operators.map((operator) => ({
                            value: operator.id,
                            label: `${operator.full_name || operator.username}${operator.department ? ` · ${operator.department}` : ''}`,
                        }))}
                        onChange={setSelectedOperatorId}
                    />
                )}
            </Space>


            <Row gutter={[24, 24]}>
                {isSuperAdmin && (
                    <Col xs={24} sm={12} lg={8} xl={8}>
                        <StatCard
                            title="TG Аккаунты"
                            value={stats?.total_accounts || 0}
                            subtitle={`${stats?.active_accounts || 0} активных`}
                            icon={<StatAccountsIcon size={36} />}
                            color="#0088cc"
                            onClick={() => navigate('/admin/accounts')}
                            delay={0}
                        />
                    </Col>
                )}

                <Col xs={24} sm={12} lg={8} xl={8}>
                    <StatCard
                        title="Операторы"
                        value={stats?.total_operators || 0}
                        subtitle={`${stats?.online_operators || 0} онлайн`}
                        icon={<StatOperatorsIcon size={36} />}
                        color="#8b5cf6"
                        onClick={() => navigate('/admin/operators')}
                        delay={0.1}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8} xl={8}>
                    <StatCard
                        title="Сообщения"
                        value={stats?.messages_today || 0}
                        subtitle={`всего ${stats?.total_messages || 0}`}
                        icon={<StatMessagesIcon size={36} />}
                        color="#10b981"
                        onClick={() => navigate('/chats')}
                        delay={0.2}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8} xl={8}>
                    <StatCard
                        title="Лиды"
                        value={stats?.total_contacts || 0}
                        subtitle={selectedOperatorId || !isAdmin ? 'закреплено' : 'в базе'}
                        icon={<StatContactsIcon size={36} />}
                        color="#f59e0b"
                        onClick={() => navigate('/contacts')}
                        delay={0.3}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8} xl={8}>
                    <StatCard
                        title="Очередь рассылок"
                        value="📨"
                        subtitle="первые сообщения"
                        icon={
                            <div style={{
                                fontSize: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                📨
                            </div>
                        }
                        color="#667eea"
                        onClick={() => navigate('/broadcast-queue')}
                        delay={0.4}
                    />
                </Col>

                {/* РАЗДЕЛЫ ОТКЛЮЧЕНЫ вместе со статусной моделью: карточки вели на «Календарь» и «Задачи».
                <Col xs={24} sm={12} lg={8} xl={8}>
                    <StatCard
                        title="Задачи"
                        value="✅"
                        subtitle="управление"
                        icon={
                            <div style={{
                                fontSize: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                📋
                            </div>
                        }
                        color="#ec4899"
                        onClick={() => navigate('/tasks')}
                        delay={0.5}
                    />
                </Col> */}
            </Row>


            <div style={{ marginTop: 40, animation: 'fadeInUp 0.5s ease-out 0.5s both' }}>
                <h2 style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    marginBottom: 20,
                }}>
                    Быстрые действия
                </h2>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div
                            onClick={() => navigate('/chats')}
                            style={{
                                padding: 20,
                                background: 'var(--bg-surface)',
                                borderRadius: 14,
                                border: '1px solid var(--border)',
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                            }}
                            className="quick-action-card"
                        >
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 20,
                            }}>
                                💬
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Открыть чаты</div>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Просмотр диалогов</div>
                            </div>
                        </div>
                    </Col>

                    {isSuperAdmin && (
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <div
                                onClick={() => navigate('/admin/accounts')}
                                style={{
                                    padding: 20,
                                    background: 'var(--bg-surface)',
                                    borderRadius: 14,
                                    border: '1px solid var(--border)',
                                    cursor: 'pointer',
                                    transition: 'all 0.25s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                }}
                                className="quick-action-card"
                            >
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: 'linear-gradient(135deg, #0088cc 0%, #00b4d8 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 20,
                                }}>
                                    📱
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Добавить аккаунт</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Загрузить TDATA</div>
                                </div>
                            </div>
                        </Col>
                    )}

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div
                            onClick={() => navigate('/contacts')}
                            style={{
                                padding: 20,
                                background: 'var(--bg-surface)',
                                borderRadius: 14,
                                border: '1px solid var(--border)',
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                            }}
                            className="quick-action-card"
                        >
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 20,
                            }}>
                                👥
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Лиды</div>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Управление базой</div>
                            </div>
                        </div>
                    </Col>

                    {isSuperAdmin && (
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <div
                                onClick={() => navigate('/admin/proxies')}
                                style={{
                                    padding: 20,
                                    background: 'var(--bg-surface)',
                                    borderRadius: 14,
                                    border: '1px solid var(--border)',
                                    cursor: 'pointer',
                                    transition: 'all 0.25s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                }}
                                className="quick-action-card"
                            >
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 10,
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 20,
                                }}>
                                    🌐
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Прокси</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Настройка сети</div>
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>
            </div>


            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }

                .stat-card-animated:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                    border-color: var(--brand-primary);
                }

                .stat-card-animated:hover .hover-line {
                    transform: scaleX(1) !important;
                }

                .stat-card-animated:hover .click-hint {
                    opacity: 1 !important;
                }

                .quick-action-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.08);
                    border-color: var(--brand-primary);
                }
            `}</style>
        </div>
    )
}
